import { Page } from 'next'
import { FeatureSection } from '~/pages/homepage/components/feature-section'
import { Hero } from '~/pages/homepage/components/hero'
import { FeatureList } from '~/pages/homepage/components/feature-list'
import { HomepageLayout } from '~/pages/homepage/layouts/homepage-layout'
import Image from 'next/image'
import { ProductImage } from '~/pages/homepage/components/product-image'
import { RetinaImage } from '~/pages/homepage/components/retina-image'

import allocationsImage from './assets/allocations.png'
import automationsEditorImage from './assets/automations-editor.png'
import donationPageImage from './assets/donation-page.png'
import donorPortalImage from './assets/donor-portal.png'
import donorsCoverFeesImage from './assets/donors-cover-fees.png'
import doubleTheDonationImage from './assets/double-the-donation.png'
import fundraisingMobileImage from './assets/fundraising-mobile.jpeg'
import paymentFormImage from './assets/payment-form.png'
import paymentOptionsImage from './assets/payment-options.png'
import peerToPeerImage from './assets/peer-to-peer.jpeg'
import radarImage from './assets/radar.png'
import customDomainImage from './assets/custom-domain.png'
import recurringGivingImage from './assets/recurring-giving.png'
import donateButtonImage from './assets/donate-button.png'
import fundraisingWebsiteImage from './assets/fundraising-website.png'
import creditCardImage from './assets/credit-card.png'
import editorImage from './assets/editor.png'
import heroImage from './assets/hero.png'

export const Fundraising: Page = () => {
  return (
    <>
      <div className="bg-[#E9F8FF]">
        <Hero
          title={'Online \nFundraising'}
          description="Collect donations through beautiful fundraising websites and donation forms. Run SMS and social media campaigns to bring more traffic and raise even more."
        >
          <div className="inline-flex lg:translate-x-8 lg:min-w-[658px]">
            <RetinaImage src={heroImage} alt="" priority />
          </div>
        </Hero>
      </div>

      <div className="border-b">
        <FeatureSection
          id="fundraising-websites"
          label="Fundraising Websites"
          title="Leave a Mark With Fundraising Website"
          description="Modern and intiutive fundraising websites are the best way to communicate your cause. Leverage the potential of our unlimited customizable pages to get more donors."
        >
          <ProductImage src={fundraisingWebsiteImage} alt="" />
        </FeatureSection>
        <FeatureList
          id="features"
          className="pb-20 md:pb-36"
          features={[
            {
              title: 'Free Image Library',
              description:
                'Take advantage of our free media library with over 2,000,000 photos and pick the perfect cover image for your page.',
            },
            {
              title: 'Powerful Story Editor',
              description:
                'Upload photos of your team, add YouTube videos, create bulleted lists and add links with our interactive story editor.',
            },
            {
              title: 'Customizable Donation Tiers',
              description:
                "Describe the impact of a donor’s contribution to your cause and increase your campaign's transparency.",
            },
            {
              title: 'Mobile Optimized',
              description:
                'Convert donations across all platforms thanks to pages that are optimized for all browsers, no matter the size or device.',
            },
            {
              title: 'Unlimited Pages',
              description:
                'Create as many pages as you need with no extra charge. Unlimited pages are available for free.',
            },
            {
              title: 'Optional Goals',
              description:
                'Define the goal amounts and help your donors understand why their support matters.',
            },
          ]}
        />
      </div>

      <FeatureSection
        id="donation-form"
        label="Donation Form"
        title="Collect Donations Instantly"
        description="Capture all your donor info — including comments, dedications and contribution allocations — from a ready-to-use universal Donation Form. Custom domain, branding, no setup needed, mobile-optimized, free of charge."
      >
        <ProductImage src={donationPageImage} alt="" />
      </FeatureSection>
      <FeatureSection
        id="peer-to-peer"
        label="Peer-to-Peer Fundraising"
        title="Expand Power of Your Team"
        description="Fundraisers are an easy way to leverage the power of your most loyal supporters and team members. Create a new page, share it with your supporters and let them fundraise on your behalf."
        reverse
      >
        <RetinaImage src={peerToPeerImage} alt="" />
      </FeatureSection>
      {/* we don't use it anymore
        <FeatureSection
          id="text-to-donate"
          label="Text-to-Donate"
          title="Capture Donations With Text-to-Donate"
          description="One of the easiest ways of mobile giving. Tell your donors to text a keyword of your choice to your phone number and they’ll receive a link with your fundraising website."
        >
          <RetinaImage src={textToDonateImage} alt="" />
        </FeatureSection>
      */}

      <div className="bg-[#FFEAD7] md:pt-20 pb-20 md:pb-36">
        <FeatureSection
          id="donor-portal"
          label="Donor Portal"
          title="Give Donors the Control They Deserve"
          description="Build better relationships with your donors using the Kindest Donor Portal. Donors can manage their donations and profiles or download receipts with a self-serve tool that saves both their time and yours."
        >
          <ProductImage src={donorPortalImage} alt="" />
        </FeatureSection>
        <FeatureList
          features={[
            {
              title: 'Download Tax Recipts',
              description:
                'No need to search through old emails. Our donor portal allows quick access to downloadable PDF tax receipts anytime.',
            },
            {
              title: 'Restart or Pause Support',
              description:
                'An easy way for donors to restart canceled monthly donations or give more simply by clicking one button.',
            },
            {
              title: 'Update Payment Method',
              description:
                'Besides automatic card renewal, the donor portal allows donors to manually update their payment method.',
            },
          ]}
        />
      </div>

      <FeatureSection
        id="donate-button"
        label="Patented Donate Button"
        title="Turn Website Visitors Into Donors"
        description="Increase the conversion of your nonprofit’s website. Our patented donate button technology provides easy access to Donation and Fundraising Pages for every new visitor of your own website."
      >
        <RetinaImage src={donateButtonImage} alt="" />
      </FeatureSection>
      <FeatureSection
        id="automatic-card-renewal"
        label="Automatic Card Renewal"
        title="Never Miss Another Donation"
        description="Credit cards expire. There’s no way around that. Luckily, it’s no longer an issue for recurring donations. After a donor’s card expires, we automatically switch to the new one without any action needed."
        reverse
      >
        <RetinaImage src={creditCardImage} alt="" />
      </FeatureSection>

      <div className="bg-[#F3F6F9] md:pt-20 pb-20 md:pb-36">
        <FeatureSection
          id="branding"
          label="Custom Branding"
          title="Make Your Brand Stand Out"
          description="Your brand is one the most important connections between you and your donors. Personalize the donor experience and let your brand color, logo and images stand out."
        >
          <ProductImage src={editorImage} alt="" />
        </FeatureSection>
        <FeatureList
          features={[
            {
              title: 'Branded Fundrasing Websites',
              description:
                'Customize the logo, brand color and buttons on your main storytelling device and make your campaign truly yours.',
            },
            {
              title: 'Branded Checkout & Receipts',
              description:
                'Keep visual communication consistent throughout the whole donation process with branded checkout and receipts.',
            },
            {
              title: 'Customizable Emails',
              description:
                'Both email automations and email campaigns offer you full customization of design as well as content.',
            },
          ]}
        />
      </div>

      <FeatureSection
        id="allocations"
        label="Contribution Allocations"
        title="Give Donors Allocation Options"
        description="Allow your donors to choose which of your programs they want to support. Create an allocation list and track where your donors want you to help the most."
      >
        <ProductImage src={allocationsImage} alt="" />
      </FeatureSection>
      <FeatureSection
        id="recurring-giving"
        label="Recurring Giving"
        title="Convert One-Time Donors Into Reccuring"
        description="Give donors the option to make their impact bigger with recurring giving. Reduce churn with automatic card renewal and increase your monthly giving with monthly conversion automation."
        reverse
      >
        <RetinaImage src={recurringGivingImage} alt="" />
      </FeatureSection>

      <div className="bg-[#FFEAD7] md:pt-20 pb-20 md:pb-36">
        <FeatureSection
          id="campaigns"
          label="SMS & Facebook Campaigns"
          title="Engage Supporters With Outbound Marketing"
          description="Text messages have a 5x higher open rate and 6x higher click-through rate than emails. Approach and convert your supporters with the most efficient outbound fundraising tools."
        >
          <ProductImage src={automationsEditorImage} alt="" />
        </FeatureSection>
        <FeatureList
          features={[
            {
              title: 'SMS Marketing',
              description:
                'With a 98% open rate, text messages are an extremely effective communication tool. Leverage their potential to your advantage.',
            },
            {
              title: 'Segmentation',
              description:
                'Send your campaigns to specific target groups like LYBUNTY/SYBUNTY or to supporters of specific pages.',
            },
            {
              title: 'Facebook Marketing',
              description:
                'Schedule and send Facebook posts to increase the impact of your fundraising websites.',
            },
          ]}
        />
      </div>

      <FeatureSection
        id="donors-covering-fees"
        label="Donor Covers Fee"
        title="94% of Our Donors Cover Fees"
        description="Thanks to our advanced UX, it’s super easy for donors to cover transaction fees and, in 94% of cases, they do. This way, you can be sure you receive the donation in full."
      >
        <RetinaImage src={donorsCoverFeesImage} alt="" />
      </FeatureSection>
      <FeatureSection
        id="mobile-optimized"
        label="Mobile Optimized"
        title="Works Great Everywhere"
        description="Whether your donors are at home, working or on the move, you can be sure that they will always be able to access your pages and make donations with our mobile optimized workflow."
        reverse
      >
        <Image
          src={fundraisingMobileImage}
          width={fundraisingMobileImage.width / 2}
          height={fundraisingMobileImage.height / 2}
          quality={100}
          alt=""
        />
      </FeatureSection>
      <FeatureSection
        id="tributes"
        label="Tributes"
        title="Let Donors Dedicate Their Donation"
        description="Allow donors to dedicate their donation in honor or memory of someone. Their dedications are automatically shared with the recipients of their choice."
      >
        <ProductImage src={paymentFormImage} alt="" />
      </FeatureSection>

      <div className="bg-[#F3F6F9] md:pt-20 pb-20 md:pb-36">
        <FeatureSection
          id="payment-options"
          label="More Payment Options"
          title="Your Donors Choose the Payment Method"
          description="Let your donors choose their preferred payment method and increase donor comfort. We support all popular payment methods, including Apple Pay, Google Pay and DAFpay."
        >
          <RetinaImage src={paymentOptionsImage} alt="" />
        </FeatureSection>
        <FeatureList
          features={[
            {
              title: 'Bank Account (ACH)',
              description:
                'ACH allow donors to transfer their funds directly from their bank account. Instant and with reduced fees.',
            },
            {
              title: 'Apple Pay & Google Pay',
              description:
                'One of the most convenient and secure ways of giving, with reduced friction thanks to pre-saved credentials.',
            },
            {
              title: 'Credit & Debit Cards',
              description:
                'We support all major credit and debit card providers, like VISA, MasterCard, Citibank, American Express and Chase.',
            },
            {
              title: 'DAFpay by Chariot',
              description:
                'Chariot built DAFpay to empower donors to give directly with their Donor Advised Fund in just three clicks with instant grant initiation and data capture. 1,100+ DAF providers supported.',
            },
          ]}
        />
      </div>

      <FeatureSection
        id="custom-domains"
        label="Custom Domains"
        title="Keep It on Your Own Domain"
        description="Host fundraising websites and donation pages, and send personalized emails right from your own domain or subdomain. Your own domain will deliver more trust and consistency to your donors."
      >
        <RetinaImage src={customDomainImage} alt="Custom domain" />
      </FeatureSection>
      <FeatureSection
        label="Automated Payouts"
        title="Secure and Automated Payouts With Stripe"
        description="Donations and payouts are securely managed by Stripe. With Stripe Radar technology and its robust insights, machine learning and fraud risk assessment methods, you can be sure that all of your finances are safe and sound."
        reverse
      >
        <RetinaImage src={radarImage} alt="Payouts" />
      </FeatureSection>
      <FeatureSection
        id="corporate-matching"
        label="Corporate Matching"
        title="Double the Donation"
        description="Our integration with Double the Donation offers a large database of matching gift programs through the 360MatchPro matching gift plugin. You’ll never miss a match opportunity again."
      >
        <RetinaImage
          src={doubleTheDonationImage}
          alt="Double the donation integration"
        />
      </FeatureSection>
    </>
  )
}

Fundraising.getLayout = HomepageLayout
